import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommunityCard from "../components/CommunityCard";
import { getCommunitiesByGuild, getGuildById } from "../data";
import { matchesQuery } from "../utils/format";

const activityScore = {
  daily: 3,
  weekly: 2,
  monthly: 1
};

const Guild = () => {
  const { guildId } = useParams();
  const guild = getGuildById(guildId);

  const communities = useMemo(() => (guild ? getCommunitiesByGuild(guild.id) : []), [guild]);
  const [filters, setFilters] = useState({
    search: "",
    platform: "all",
    region: "all",
    tag: "all",
    sort: "featured"
  });

  const filteredCommunities = useMemo(() => {
    if (!guild) return [];
    return communities
      .filter((community) => {
        if (filters.search && !matchesQuery(`${community.name} ${community.summary}`, filters.search)) return false;
        if (filters.platform !== "all" && !community.platforms.some((platform) => platform.type === filters.platform))
          return false;
        if (filters.region !== "all" && community.region !== filters.region) return false;
        if (filters.tag !== "all" && !community.tags.includes(filters.tag)) return false;
        return true;
      })
      .sort((a, b) => {
        if (filters.sort === "featured") return Number(b.featured) - Number(a.featured);
        if (filters.sort === "activity") return (activityScore[b.activity] || 0) - (activityScore[a.activity] || 0);
        if (filters.sort === "members") return b.memberCount - a.memberCount;
        if (filters.sort === "newest")
          return new Date(b.launched).getTime() - new Date(a.launched).getTime();
        return 0;
      });
  }, [communities, filters, guild]);

  if (!guild) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl text-slate-100">Guild not found</h1>
        <Link to="/directory" className="text-brand-primary underline-offset-4 hover:underline">
          Return to directory
        </Link>
      </div>
    );
  }

  const regionOptions = Array.from(new Set(communities.map((community) => community.region))).sort();
  const platformOptions = Array.from(
    new Set(communities.flatMap((community) => community.platforms.map((platform) => platform.type)))
  ).sort();
  const tagOptions = Array.from(new Set(communities.flatMap((community) => community.tags))).sort();

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const uniqueTags = Array.from(new Set(communities.flatMap((community) => community.tags)));
  const cityCount = new Set(communities.map((community) => community.city)).size;

  const heroMetrics = [
    {
      label: "Communities",
      value: guild.communitiesCount,
      helper: "Verified local chapters"
    },
    {
      label: "Platforms",
      value: guild.platforms.length,
      helper: "Collaboration hubs"
    },
    {
      label: "Members",
      value: guild.memberCount.toLocaleString(),
      helper: "Across programs"
    }
  ];

  const guildSignals = [
    {
      title: "Operating regions",
      copy: `${guild.regionFocus} • Active in ${cityCount} cities`
    },
    {
      title: "Program spotlight",
      copy: guild.spotlight
    },
    {
      title: "Tags in focus",
      copy: uniqueTags.slice(0, 5).join(", ")
    },
    {
      title: "Platform mix",
      copy: guild.platforms.join(" • ")
    }
  ];

  return (
    <div className="space-y-10">
      <section className="panel space-y-6">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-brand-steel">
            <span className="chip-soft" style={{ borderColor: guild.accentColor, color: guild.accentColor }}>
              {guild.category}
            </span>
            <span>{guild.regionFocus}</span>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <span
              className="grid h-16 w-16 place-content-center rounded-3xl border border-brand-ink/50 bg-brand-night text-lg font-bold text-slate-100"
              style={{ color: guild.accentColor }}
            >
              {guild.logo}
            </span>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{guild.platforms.join(" • ")}</p>
              <h1 className="text-4xl text-slate-100">{guild.name}</h1>
              <p className="text-base text-slate-300">{guild.description}</p>
            </div>
          </div>
          <p className="text-sm text-slate-400">{guild.spotlight}</p>
        </div>
        <div className="grid gap-4 rounded-[28px] border border-brand-ink/40 bg-brand-night/70 p-5 text-sm text-slate-200 sm:grid-cols-3">
          {heroMetrics.map((metric) => (
            <dl key={metric.label} className="space-y-2">
              <dt className="text-[11px] uppercase tracking-[0.3em] text-brand-steel">{metric.label}</dt>
              <dd className="text-3xl font-semibold text-brand-highlight">{metric.value}</dd>
              <dd className="text-xs text-slate-500">{metric.helper}</dd>
            </dl>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link to={`/guilds/${guild.id}/map`} className="btn-primary">
            Open guild mini-map
          </Link>
          <Link to="/map" className="btn-secondary">
            Back to global map
          </Link>
          <Link to="/directory" className="btn-secondary">
            Directory
          </Link>
        </div>
      </section>

      <div className="section-wrapper">
        <section className="grid gap-4 md:grid-cols-2">
          {guildSignals.map((signal) => (
            <article key={signal.title} className="panel space-y-2 text-sm text-slate-300">
              <p className="text-xs uppercase tracking-[0.35em] text-brand-steel">{signal.title}</p>
              <p>{signal.copy}</p>
            </article>
          ))}
        </section>
      </div>

      <div className="section-wrapper">
        <section className="panel space-y-4">
          <div className="space-y-2">
            <p className="eyebrow">Explore communities</p>
            <p className="text-sm text-slate-400">Search, filter, and sort across the entire guild.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Search</label>
              <input
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                placeholder="Search by community name, summary, or tag"
                className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100 placeholder:text-brand-steel"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Sort</label>
              <select
                value={filters.sort}
                onChange={(event) => updateFilter("sort", event.target.value)}
                className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
              >
                <option value="featured">Featured first</option>
                <option value="activity">Most active</option>
                <option value="members">Most members</option>
                <option value="newest">Newest</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Platform</label>
              <select
                value={filters.platform}
                onChange={(event) => updateFilter("platform", event.target.value)}
                className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
              >
                <option value="all">All platforms</option>
                {platformOptions.map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Region</label>
              <select
                value={filters.region}
                onChange={(event) => updateFilter("region", event.target.value)}
                className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
              >
                <option value="all">All regions</option>
                {regionOptions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Tags</label>
              <select
                value={filters.tag}
                onChange={(event) => updateFilter("tag", event.target.value)}
                className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
              >
                <option value="all">All tags</option>
                {tagOptions.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-sm text-slate-400">{filteredCommunities.length} communities match these filters.</p>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="grid gap-6 md:grid-cols-2">
          {filteredCommunities.map((community) => (
            <CommunityCard key={community.id} community={community} guild={guild} />
          ))}
          {filteredCommunities.length === 0 && <p className="text-slate-400">No communities match those filters.</p>}
        </section>
      </div>
    </div>
  );
};

export default Guild;
